import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import ListGroup from 'react-bootstrap/ListGroup'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import UserContext from 'context/user-context'
import Search from 'shared/forms/search'
import { useFolderContext, withFolderContext } from 'dashboard/folders/folder-context'
import { groupDimensions } from '../account-groups/scopes'
import { splitColumnDescription } from './split-column'
import {
  useReports,
  useSplitOptions,
  useSplitCounts,
  useSplitReport,
  useRegenerateReportText
} from 'api/hooks/use-report-builder'

const ACCOUNT_GROUPS = 'account_groups'

// Fans a report out into one read-only child per value of one account segment — or per account
// group, the municipality's own named sets of codes — and lists the children it already has. Both
// in one place: seeing which slices exist and deciding to add another are the same task. A viewer
// gets the list without the picker.
const SplitReportModal = ({ report, show, onHide, onSplit, canEdit = true }) => {
  const user = useContext(UserContext)
  const queryClient = useQueryClient()
  const settings = user?.municipality?.setting
  // Children are filed with the parent, so opening one keeps the folder the parent was opened
  // from — the child's back link lands there rather than at the top of the index.
  const folderId = useFolderContext()

  // A report already split on a column is extended along that column by default: it describes
  // every child it already has. Switching columns is allowed, but it replaces them.
  const existingCount = report?.child_reports_count || 0
  const lockedDimension = existingCount > 0 ? report?.split_dimension : null
  const lockedSegment = existingCount > 0 ? report?.split_segment : null

  const segments = useMemo(() => groupDimensions(settings), [settings])
  // One flat list: every account segment under its municipality name, then the groups built on
  // top of them.
  const columns = useMemo(
    () => [
      ...segments.map((s) => ({ key: `segment:${s.key}`, label: s.label })),
      { key: ACCOUNT_GROUPS, label: 'Account groups' }
    ],
    [segments]
  )

  const initialColumn = lockedDimension === 'account_group' ? ACCOUNT_GROUPS : `segment:${lockedSegment}`
  const defaultColumn = lockedDimension ? initialColumn : columns[0]?.key

  const [column, setColumn] = useState(defaultColumn)
  // Search owns a params object because that is the contract shared/forms/search expects.
  const [searchParams, setSearchParams] = useState({ search: '' })
  const [selected, setSelected] = useState([])
  const [hideEmpty, setHideEmpty] = useState(false)
  const search = searchParams.search

  // Shift-click selects the whole stretch since the last value clicked. The anchor is an id, not
  // an index — the list re-filters under the search box and the hide-empty switch, and an id
  // survives that where an index silently points at a different row.
  const anchorRef = useRef(null)
  // The modifier is read at mousedown on the row: the click a label forwards to its checkbox
  // does not reliably carry it.
  const shiftRef = useRef(false)

  // This modal is mounted for the life of the page, so its state outlives one opening. Without
  // this, a report split a moment ago reopens on the column it was on *before* the split — which
  // reads as "replace the children you just made", red alert and red submit button to match, and
  // submitting destroys them. Only on open: a background refetch must not wipe a selection the
  // user is in the middle of making.
  useEffect(() => {
    if (!show) return
    setColumn(defaultColumn)
    setSelected([])
    setSearchParams({ search: '' })
    anchorRef.current = null
    // `show` alone on purpose — see above.
  }, [show])

  const isGroupSplit = column === ACCOUNT_GROUPS
  const dimension = isGroupSplit ? 'account_group' : 'account_segment'
  const segment = isGroupSplit ? undefined : column.replace('segment:', '')
  // Changing the column means the existing children describe one the report no longer splits on,
  // so they are replaced rather than left orphaned.
  const replacesChildren =
    existingCount > 0 && (dimension !== lockedDimension || segment !== (lockedSegment || undefined))

  const {
    data: optionData,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useSplitOptions({
    reportId: report?.id,
    dimension,
    segment,
    search,
    options: { enabled: !!report?.id && show, keepPreviousData: true }
  })
  // isFetching covers loading the next page too, and the emptiness the messages below speak for
  // is about the list as a whole — a page arriving mid-scroll must not read as one starting over.
  const isLoadingValues = isFetching && !isFetchingNextPage

  // A separate request from the values themselves, because it is a separate kind of work: the
  // values are a lookup and the counts are a scan of every transaction in the report. The list
  // renders the moment it arrives and these land on top of it — searching never waits on them,
  // and never re-runs them either, since a count describes a value rather than a search.
  const { data: countData, isFetching: isCounting } = useSplitCounts({
    reportId: report?.id,
    dimension,
    segment,
    options: {
      enabled: !!report?.id && show,
      // The scan is expensive and its answer barely moves, so one sitting pays for it once
      // rather than on every reopen and every window refocus.
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  })
  // The counts are in hand only when the scan actually answered. Still running and came back
  // empty-handed are both "not in hand", and neither may be read as a zero: calling every value
  // empty on the strength of a query that never answered is what steers a user off a perfectly
  // valid split.
  const countsKnown = countData?.known === true
  // How many values reach this report's data at all. Zero means the column can't describe this
  // report, whatever the user picks.
  const populatedCount = countsKnown ? countData.populated_count : null
  // Unknown *and* settled, so the "counts are unavailable" note is about a failure rather than
  // about a request still in flight.
  const countsFailed = !!countData && !countsKnown

  // Every page loaded so far, flattened. Decorated rather than merged server-side: an option
  // carries no count of its own, and a value the counts don't mention claims nothing.
  const options = useMemo(() => {
    const values = optionData?.pages?.flatMap((page) => page.data || []) || []
    if (!countsKnown) return values
    return values.map((option) => ({ ...option, count: countData.counts?.[option.id] || 0 }))
  }, [optionData, countData, countsKnown])

  // Every value the column has, not just the ones loaded — what the list is a window onto.
  const totalValues = optionData?.pages?.[0]?.meta?.total ?? options.length

  // Scrolling to the bottom of the list is the ordinary way to reach the next page; the button
  // below it is the same thing for anyone not using a mouse to get there.
  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }

  const handleListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop - clientHeight < 48) loadMore()
  }

  const selectedEmpty = countsKnown
    ? selected.filter((id) => {
      const option = options.find((o) => o.id === id)
      return option && !option.count
    }).length
    : 0

  const visibleOptions = hideEmpty && countsKnown ? options.filter((o) => o.count) : options
  const visibleIds = visibleOptions.map((o) => o.id)
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id))
  const someVisibleSelected = visibleIds.some((id) => selected.includes(id))

  const { mutate: splitReport, isLoading: isSplitting } = useSplitReport({ options: {} })

  // The children this report already has, loaded only while the modal is open.
  const { data: childData, isFetching: isLoadingChildren } = useReports({
    // Twice Report::MAX_CHILDREN, so the list can never silently truncate.
    params: { parent_report_id: report?.id, perPage: 200 },
    options: { enabled: !!report?.id && existingCount > 0 && show }
  })
  const existingChildren = childData?.data || []
  const existingDescription = splitColumnDescription(report, settings)
  const existingNoun = `child report${existingCount === 1 ? '' : 's'}`

  const { mutate: regenerateText, isLoading: isRegenerating } = useRegenerateReportText({ options: {} })

  // Each child's written sections are rewritten against its own figures. Offered here because
  // this is where the children are, and because editing the template's prose leaves every child's
  // copy describing the previous version.
  const handleRegenerateAll = () => {
    regenerateText(report.id, {
      onSuccess: () => toast.success(`Rewriting narrative for ${existingCount} ${existingNoun}`),
      onError: () => toast.error('Could not start the rewrite')
    })
  }

  const handleCheck = (option, index, shift) => {
    shiftRef.current = false
    const anchorIndex = visibleOptions.findIndex((o) => o.id === anchorRef.current)
    setSelected((current) => {
      const isSelected = current.includes(option.id)
      if (shift && anchorIndex !== -1) {
        const [from, to] = anchorIndex < index ? [anchorIndex, index] : [index, anchorIndex]
        const rangeIds = visibleOptions.slice(from, to + 1).map((o) => o.id)
        // The click's own effect — selecting or deselecting — applies to the whole stretch.
        return isSelected
          ? current.filter((id) => !rangeIds.includes(id))
          : [...new Set([...current, ...rangeIds])]
      }
      return isSelected ? current.filter((id) => id !== option.id) : [...current, option.id]
    })
    anchorRef.current = option.id
  }

  // Over the values currently shown, so with the empty ones hidden this selects only values that
  // have data — the two controls compose into "select everything worth splitting on".
  const toggleAll = () => {
    setSelected((current) =>
      allVisibleSelected
        ? current.filter((id) => !visibleIds.includes(id))
        : [...new Set([...current, ...visibleIds])]
    )
    anchorRef.current = null
  }

  const changeColumn = (next) => {
    setColumn(next)
    setSelected([])
    setSearchParams({ search: '' })
    anchorRef.current = null
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    splitReport(
      {
        reportId: report.id,
        dimension,
        segment,
        value_ids: selected,
        replace_existing: replacesChildren
      },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries(['reports-builder'])
          queryClient.invalidateQueries([`reports/${report.id}`])
          toast.success(`Created ${data?.children?.length || 0} child report(s)`)
          setSelected([])
          onSplit?.(data)
          onHide()
        },
        onError: (error) => toast.error(error?.response?.data?.message || 'Could not split the report')
      }
    )
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title className="h5">{existingCount > 0 ? 'Child reports' : 'Split report'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {existingCount > 0 && (
            <>
              <div className="d-flex align-items-center gap-2 mb-2">
                <p className="text-muted mb-0 flex-grow-1">
                  {existingCount} {existingNoun} split off of {existingDescription}.
                </p>
                {canEdit && (
                  <Button
                    variant="light"
                    size="sm"
                    className="text-primary border"
                    onClick={handleRegenerateAll}
                    disabled={isRegenerating}
                  >
                    {isRegenerating ? 'Starting…' : 'Rewrite narratives'}
                  </Button>
                )}
              </div>
              <div className="border rounded mb-4" style={{ maxHeight: 200, overflowY: 'auto' }}>
                {isLoadingChildren && existingChildren.length === 0 ? (
                  <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                ) : (
                  <ListGroup variant="flush">
                    {existingChildren.map((child) => (
                      <ListGroup.Item
                        key={child.id}
                        action
                        as={Link}
                        to={withFolderContext(`/reports/${child.id}`, folderId)}
                      >
                        {child.split?.value_label || child.name}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </div>
            </>
          )}

          {!canEdit ? (
            <p className="text-muted mb-0">Only an editor can add or replace child reports.</p>
          ) : (
            <>
              <h6>{existingCount > 0 ? 'Add more' : 'Split this report'}</h6>
              <p className="text-muted">
                Each value you pick gets its own read-only copy of this report, showing only its own
                data. Edits you make here flow down to all of them.
              </p>

              <Form.Group className="mb-3">
                <Form.Label>Split by</Form.Label>
                <Form.Select value={column} onChange={(e) => changeColumn(e.target.value)}>
                  {columns.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </Form.Select>
                {existingCount > 0 && !replacesChildren && (
                  <Form.Text className="text-muted">
                    This report is already split by this column — new values are added to its{' '}
                    {existingCount} existing child report{existingCount === 1 ? '' : 's'}.
                  </Form.Text>
                )}
              </Form.Group>

              {replacesChildren && (
                <Alert variant="warning" className="py-2">
                  <strong>
                    This replaces the {existingCount} existing child report
                    {existingCount === 1 ? '' : 's'}.
                  </strong>{' '}
                  They describe the column this report is split by today, so switching columns
                  deletes them and creates a new set. This can&apos;t be undone.
                </Alert>
              )}

              {populatedCount === 0 && !isLoadingValues && (
                <Alert variant="danger" className="py-2">
                  <strong>Nothing in this report reaches it through this column.</strong> Every
                  child would come out empty. Try another column.
                </Alert>
              )}

              {countsFailed && !isLoadingValues && options.length > 0 && (
                <Alert variant="light" className="py-2 border">
                  <strong>Transaction counts aren&apos;t available right now.</strong> The values
                  below are still correct — but which of them have data in this report is unknown,
                  so check a child after it is created.
                </Alert>
              )}

              <Form.Group className="mb-2">
                <Form.Label className="d-flex align-items-center justify-content-between">
                  <span>Values</span>
                  {/* The scan runs alongside the list rather than in front of it, so this is the
                      one place that admits it is still going — the rows below are already usable. */}
                  {isCounting && !countData && (
                    <span className="text-muted fw-normal small">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Checking which values have data…
                    </span>
                  )}
                  {populatedCount !== null && populatedCount > 0 && (
                    <span className="text-muted fw-normal small">
                      {populatedCount} with data in this report
                    </span>
                  )}
                </Form.Label>
                <Search params={searchParams} setParams={setSearchParams} placeholder="Search values" />
                <Form.Text className="text-muted">
                  Use * as a wildcard — 03* matches every code starting with 03.
                </Form.Text>
              </Form.Group>

              <div className="d-flex align-items-center justify-content-between mb-2">
                {/* Over the values *shown*, which with a partly-loaded list is not every value the
                    column has — so the count is in the label rather than left to be assumed. */}
                <Form.Check
                  type="checkbox"
                  id="split-select-all"
                  label={`Select all${visibleOptions.length ? ` (${visibleOptions.length})` : ''}`}
                  checked={allVisibleSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected
                  }}
                  onChange={toggleAll}
                  disabled={visibleOptions.length === 0}
                />
                <Form.Check
                  type="switch"
                  id="split-hide-empty"
                  label="Hide values with no data"
                  checked={hideEmpty && countsKnown}
                  disabled={!countsKnown}
                  onChange={(e) => setHideEmpty(e.target.checked)}
                />
              </div>

              <div
                className="border rounded user-select-none"
                data-testid="split-values"
                style={{ maxHeight: 280, overflowY: 'auto' }}
                onScroll={handleListScroll}
              >
                {isLoadingValues && options.length === 0 && (
                  <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                )}
                {!isLoadingValues && options.length === 0 && (
                  <p className="text-muted mb-0 py-2 text-center">No values found</p>
                )}
                {/* Only about the values loaded so far while there are more to come: the ones
                    with data may simply be further down the segment. */}
                {!isLoadingValues && options.length > 0 && visibleOptions.length === 0 && (
                  <p className="text-muted mb-0 py-2 text-center">
                    {hasNextPage
                      ? `No values with data in the first ${options.length} — keep loading`
                      : 'No values with data in this report'}
                  </p>
                )}
                <ListGroup variant="flush">
                  {visibleOptions.map((option, index) => (
                    <ListGroup.Item
                      key={option.id}
                      action
                      as="label"
                      className="d-flex align-items-center gap-2 mb-0"
                      onMouseDown={(e) => {
                        shiftRef.current = e.shiftKey
                      }}
                    >
                      <Form.Check.Input
                        type="checkbox"
                        id={`split-value-${option.id}`}
                        className="mt-0 flex-shrink-0"
                        checked={selected.includes(option.id)}
                        onChange={(e) =>
                          handleCheck(option, index, shiftRef.current || !!e.nativeEvent.shiftKey)
                        }
                      />
                      <span className={countsKnown && !option.count ? 'text-muted' : ''}>
                        {option.label}
                        {/* Which segment a group is built on — two segments can each hold a
                            group of the same name. */}
                        {option.sublabel && <span className="ms-2 small text-muted">{option.sublabel}</span>}
                        {/* Nothing at all when the count is unknown — "no data in this report" is
                            a claim, and an unknown count can't make it. */}
                        {countsKnown && (
                          <span className="ms-2 small">
                            {option.count
                              ? `${option.count.toLocaleString()} transactions`
                              : 'no data in this report'}
                          </span>
                        )}
                      </span>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
                {/* Scrolling here loads the next page on its own; the button is the same reach for
                    anyone arriving by keyboard, and says how far into the column the list has got. */}
                {hasNextPage && (
                  <div className="text-center py-2 border-top">
                    <Button
                      variant="light"
                      size="sm"
                      className="text-primary"
                      onClick={loadMore}
                      disabled={isFetchingNextPage}
                    >
                      {isFetchingNextPage
                        ? 'Loading…'
                        : `Load more — ${options.length} of ${totalValues.toLocaleString()}`}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {canEdit && (
            <span className="me-auto text-muted">
              {selected.length} selected
              {selectedEmpty > 0 && (
                <span className="text-danger ms-2">
                  — {selectedEmpty} with no data, {selectedEmpty === 1 ? 'it' : 'they'} will be empty
                </span>
              )}
            </span>
          )}
          <Button variant="light-primary" onClick={onHide}>{canEdit ? 'Cancel' : 'Close'}</Button>
          {canEdit && (
            <Button
              variant={replacesChildren ? 'danger' : 'primary'}
              type="submit"
              disabled={selected.length === 0 || isSplitting}
            >
              {isSplitting && 'Creating…'}
              {!isSplitting && replacesChildren && `Replace with ${selected.length} child report${selected.length === 1 ? '' : 's'}`}
              {!isSplitting && !replacesChildren && `Create ${selected.length || ''} child report${selected.length === 1 ? '' : 's'}`}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default SplitReportModal
